import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useCustomDialog } from '../../context/DialogContext';
import Cropper from 'react-easy-crop';

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

const getCroppedImg = async (imageSrc, pixelCrop) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );
  return new Promise((resolve) => {
    canvas.toBlob((file) => {
      resolve(file);
    }, 'image/jpeg');
  });
};

const LockedFeaturePage = ({ title, icon, description }) => (
    <div className="admin-card" style={{ textAlign: 'center', padding: '60px 40px' }}>
        <Link to="/manage_v2_secure_auth/" className="sticky-back-btn">
    <i className="fas fa-arrow-left"></i> Back to Dashboard
</Link>
        <div style={{ width: '72px', height: '72px', background: 'linear-gradient(135deg, #fef3c7, #fde68a)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <i className={`fas ${icon}`} style={{ fontSize: '1.8rem', color: '#f59e0b' }} />
        </div>
        <h2 style={{ margin: '0 0 10px 0' }}>{title}</h2>
        <p style={{ color: 'var(--text-color-secondary)', marginBottom: '28px', maxWidth: '400px', margin: '0 auto 28px', lineHeight: '1.65' }}>{description}</p>
        <Link to="/manage_v2_secure_auth/subscription" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 28px', background: 'var(--primary-orange, #ea580c)', color: '#fff', borderRadius: '50px', fontWeight: '700', textDecoration: 'none', boxShadow: '0 4px 14px rgba(234,88,12,0.3)' }}>
            <i className="fas fa-crown" /> Upgrade to Unlock
        </Link>
    </div>
);

const ManageOffers = ({ token, features }) => {
    const { alert: customAlert, confirm: customConfirm } = useCustomDialog();
    // We map offers as an object, e.g., { 1: "url", 2: "url", 3: "url" }
    const [offers, setOffers] = useState({});
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState({ text: '', type: '' });
    const [previewImages, setPreviewImages] = useState({ 1: null, 2: null, 3: null });
    const [files, setFiles] = useState({ 1: null, 2: null, 3: null });
    const [uploading, setUploading] = useState({ 1: false, 2: false, 3: false });

    // Cropper State
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [cropModalOpen, setCropModalOpen] = useState(false);
    const [activeCropSlot, setActiveCropSlot] = useState(null);
    const [croppingImage, setCroppingImage] = useState(null);
    const [croppingFileName, setCroppingFileName] = useState('');

    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const hasOffers = features?.has_offer_popup;

    useEffect(() => {
        if (hasOffers) {
            fetchOffers();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasOffers]);

    if (features && !features.has_offer_popup) {
        return <LockedFeaturePage title="Special Offers" icon="fa-tags" description="Upload up to 3 promotional offer images that appear as a popup when customers open your menu. Available on the Paid Plan." />;
    }

    const fetchOffers = async () => {
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_offers.php?action=fetch', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) setOffers(data.offers);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (e, slot) => {
        const file = e.target.files[0];
        if (file) {
            setCroppingFileName(file.name);
            const reader = new FileReader();
            reader.onload = (e) => {
                setCroppingImage(e.target.result);
                setActiveCropSlot(slot);
                setCropModalOpen(true);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleCropSave = async () => {
        try {
            const croppedBlob = await getCroppedImg(croppingImage, croppedAreaPixels);
            const croppedFile = new File([croppedBlob], croppingFileName, { type: 'image/jpeg' });
            
            setFiles(prev => ({ ...prev, [activeCropSlot]: croppedFile }));
            const previewUrl = URL.createObjectURL(croppedBlob);
            setPreviewImages(prev => ({ ...prev, [activeCropSlot]: previewUrl }));
            
            setCropModalOpen(false);
            setCroppingImage(null);
            setActiveCropSlot(null);
        } catch (e) {
            console.error(e);
            customAlert("Error cropping image.", "Error", "error");
        }
    };
    
    const handleCropCancel = () => {
        setCropModalOpen(false);
        setCroppingImage(null);
        setActiveCropSlot(null);
        const fileInput = document.getElementById(`file_input_${activeCropSlot}`);
        if(fileInput) fileInput.value = '';
    };

    const handleUpload = async (slot) => {
        if (!files[slot]) {
            await customAlert("Select a file first.", "Validation Error", "warning");
            return;
        }

        setUploading(prev => ({ ...prev, [slot]: true }));
        const formData = new FormData();
        formData.append('action', 'upload');
        formData.append('slot_number', slot);
        formData.append('offer_image', files[slot]);

        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_offers.php', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }, // FormData automatically sets content dict boundaries
                body: formData
            });
            const data = await res.json();
            if (data.success) {
                setMessage({ text: `Slot ${slot} uploaded successfully!`, type: 'success' });
                fetchOffers();
                setFiles(prev => ({ ...prev, [slot]: null }));
                setPreviewImages(prev => ({ ...prev, [slot]: null })); // clear draft
            } else {
                setMessage({ text: data.message, type: 'error' });
            }
        } catch (err) {
            setMessage({ text: 'Error uploading image.', type: 'error' });
        } finally {
            setUploading(prev => ({ ...prev, [slot]: false }));
            setTimeout(() => setMessage({text:'', type:''}), 3000);
        }
    };

    const handleDelete = async (slot) => {
        if (!await customConfirm(`Delete offer in slot ${slot}?`, 'Delete Offer', 'danger')) return;
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_offers.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ action: 'delete', slot_number: slot })
            });
            const data = await res.json();
            if (data.success) {
                setMessage({ text: `Slot ${slot} deleted.`, type: 'success' });
                fetchOffers();
            }
        } catch (err) {
            setMessage({ text: 'Error deleting image.', type: 'error' });
        } finally {
            setTimeout(() => setMessage({text:'', type:''}), 3000);
        }
    }

    // Helper: Legacy DB saves URLs as ../uploads/...
    const fixUrl = (url) => {
        if (url.startsWith('../')) {
            let fixed = `https://menu.searchisend.in/${url.substring(3)}`;
            return fixed.replace('/uploads/offers/', '/uploads/offer_images/');
        }
        return url;
    }

    return (
        <div className="admin-card">
            <Link to="/manage_v2_secure_auth/" className="sticky-back-btn">
                <i className="fas fa-arrow-left"></i> Back to Dashboard
            </Link>
            <h1 style={{marginTop: '0'}}>Manage Special Offers</h1>
            <p style={{marginBottom: '35px', color: 'var(--text-color-secondary)'}}>Upload up to 3 images for the offer pop-up on your menu. Use a square image with a 1:1 aspect ratio.</p>

            {message.text && (
                <div style={{padding: '15px', borderRadius: '5px', marginBottom: '20px', fontWeight: '500', backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da', color: message.type === 'success' ? '#155724' : '#721c24'}}>
                    {message.text}
                </div>
            )}

            {loading ? <p>Loading offers...</p> : (
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '30px'}}>
                    {[1, 2, 3].map(slot => (
                        <div key={slot} style={{border: '2px dashed var(--border-color)', borderRadius: '10px', padding: '20px', textAlign: 'center', backgroundColor: 'var(--bg-color)'}}>
                            <h3 style={{marginTop: 0}}>Offer Slot {slot}</h3>
                            
                            <div style={{width: '100%', aspectRatio: '1/1', backgroundColor: '#f3f4f6', borderRadius: '5px', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden'}}>
                                {previewImages[slot] ? (
                                    <img src={previewImages[slot]} alt={`Preview ${slot}`} style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                                ) : offers[slot] ? (
                                    <img src={fixUrl(offers[slot])} alt={`Offer ${slot}`} style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                                ) : (
                                    <span style={{color: '#9ca3af', fontWeight: '500'}}>No Image</span>
                                )}
                            </div>

                            {offers[slot] && !previewImages[slot] && (
                                <button onClick={() => handleDelete(slot)} style={{backgroundColor: 'var(--red-accent)', color: 'white', border: 'none', padding: '8px 15px', borderRadius: '50px', fontWeight: '600', cursor: 'pointer', marginBottom: '20px'}}>
                                    Delete Current Offer
                                </button>
                            )}
                            
                            <div style={{textAlign: 'left', padding: '15px', border: '1px solid var(--border-color)', borderRadius: '8px', backgroundColor: '#fff'}}>
                                <label style={{display: 'block', marginBottom: '8px', fontSize: '0.95em', fontWeight: '600'}}>
                                    {offers[slot] ? 'Replace Image:' : 'Upload Image:'}
                                </label>
                                <input id={`file_input_${slot}`} type="file" accept="image/*" onChange={(e) => handleFileChange(e, slot)} style={{width: '100%', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '5px', boxSizing: 'border-box', marginBottom: '15px'}} />
                                <button onClick={() => handleUpload(slot)} disabled={uploading[slot] || !files[slot]} style={{backgroundColor: files[slot] ? 'var(--primary-orange)' : '#ccc', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '50px', fontWeight: '600', cursor: files[slot] ? 'pointer' : 'not-allowed', width: '100%'}}>
                                    {uploading[slot] ? 'Uploading...' : `Upload to Slot ${slot}`}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {cropModalOpen && (
                <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)', zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px', boxSizing: 'border-box'}}>
                    <div style={{width: '100%', maxWidth: '550px', backgroundColor: '#fff', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column'}}>
                        
                        <div style={{padding: '20px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                            <h3 style={{margin: 0, fontSize: '1.2rem', color: '#111827'}}><i className="fas fa-crop-alt" style={{marginRight: '8px', color: 'var(--primary-orange)'}}></i>Crop Image (1:1)</h3>
                            <button onClick={handleCropCancel} style={{background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#6b7280'}}><i className="fas fa-times"></i></button>
                        </div>
                        
                        <div style={{position: 'relative', width: '100%', height: '400px', backgroundColor: '#f3f4f6'}}>
                            <Cropper
                                image={croppingImage}
                                crop={crop}
                                zoom={zoom}
                                aspect={1}
                                onCropChange={setCrop}
                                onCropComplete={onCropComplete}
                                onZoomChange={setZoom}
                            />
                        </div>
                        
                        <div style={{padding: '20px', backgroundColor: '#fff'}}>
                            <div style={{display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px'}}>
                                <i className="fas fa-search-minus" style={{color: '#9ca3af'}}></i>
                                <input
                                    type="range"
                                    value={zoom}
                                    min={1}
                                    max={3}
                                    step={0.1}
                                    aria-labelledby="Zoom"
                                    onChange={(e) => setZoom(e.target.value)}
                                    style={{flex: 1, accentColor: 'var(--primary-orange)'}}
                                />
                                <i className="fas fa-search-plus" style={{color: '#9ca3af'}}></i>
                            </div>
                            
                            <div style={{display: 'flex', gap: '15px'}}>
                                <button onClick={handleCropCancel} style={{flex: 1, padding: '12px 20px', borderRadius: '50px', border: '1px solid #d1d5db', cursor: 'pointer', fontWeight: '600', backgroundColor: '#fff', color: '#374151', fontSize: '1rem', transition: 'all 0.2s'}}>Cancel</button>
                                <button onClick={handleCropSave} style={{flex: 1, padding: '12px 20px', borderRadius: '50px', border: 'none', cursor: 'pointer', fontWeight: '600', backgroundColor: 'var(--primary-orange)', color: 'white', fontSize: '1rem', transition: 'all 0.2s', boxShadow: '0 4px 12px rgba(234, 88, 12, 0.3)'}}><i className="fas fa-check" style={{marginRight: '8px'}}></i>Crop & Save</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ManageOffers;
